import PropTypes from 'prop-types';
import { Helmet } from 'core/libs/helmet';
import { useEffect, useRef, useState } from 'react';
import get from 'lodash.get';
import cx from 'classnames';

import withRequestId from 'core/hocs/withRequestId';
import withResolveDomId from 'core/components/Ad/resolve/domid';

import {
  RENDER_STATE_ATTR_NAME,
  IDLE,
  PENDING,
  RENDERED,
  NOT_EXIST,
  NAME_ATTR_NAME,
  REINITIALIZING_RENDERED,
} from 'core/components/Ad';

import styles from 'core/components/Ad/index.styl';

const placeholderStates = [
  IDLE,
  PENDING,
];

const fallbackStates = [
  NOT_EXIST,
];

const DefaultPlaceholder = () => <div className={styles.defaultPlaceholder} />;

export const mapPlatformTypes = {
  adfox: {
    renderMethod: 'Ya.adfoxCode.create',
    domIdProperty: 'containerId',
    blockIdProperty: 'ownerId',
  },
  yandex: {
    renderMethod: 'Ya.Context.AdvManager.render',
    domIdProperty: 'renderTo',
    blockIdProperty: 'blockId',
  },
};

/**
 * Документация о работе ADFOX
 * https://yandex.ru/support/adfox-sites/codes/change-code.html
 *
 * По работе с контекстом
 * https://yandex.ru/support/partner/web/units/tag-features.html
 */
function Adfox(props) {
  const {
    ownerId,
    params,
    fallback: Fallback,
    placeholder: Placeholder,
    domId,
    height,
    name,
    onRender,
    blockId,
    platformType,
    lazyLoad,
  } = props;

  const adsEl = useRef(null);
  const [bannerState, setBannerState] = useState(IDLE);

  useEffect(() => {
    const {
      renderMethod,
      domIdProperty,
      blockIdProperty,
    } = mapPlatformTypes[platformType];

    try {
      window.yaContextCb.push(() => {
        const renderMethodFunc = get(window, renderMethod);

        renderMethodFunc({
          [blockIdProperty]: blockId || ownerId,
          [domIdProperty]: domId,
          params,
          lazyLoad,
          onLoad: () => {
            setBannerState(PENDING);
          },
          onRender: () => {
            if (typeof onRender === 'function') onRender();
            setBannerState(RENDERED);
          },
          onStub: () => {
            setBannerState(NOT_EXIST);
          },
          onError: (error) => {
            setBannerState(NOT_EXIST);
            console.error(error);
          },
          isAutoReloads: false,
        });
      });
    } catch (error) {
      setBannerState(NOT_EXIST);
      console.error(error);
    }

    return () => {
      window?.Ya?.adfoxCode?.destroy(domId);
      window?.Ya?.Context?.AdvManager?.destroy({ blockId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, blockId, domId, params, name]);

  const showFallback = !!Fallback && fallbackStates.includes(bannerState);
  const showPlaceholder = !!Placeholder && placeholderStates.includes(bannerState);

  const isAdVisible =  (height && (showFallback || showPlaceholder))
    || bannerState === RENDERED
    || bannerState === REINITIALIZING_RENDERED;

  return (
    <>
      <Helmet>
        <script>{'window.yaContextCb=window.yaContextCb||[];'}</script>
        <script src='https://yandex.ru/ads/system/context.js' async />
      </Helmet>
      <div
        className={cx(styles.container, isAdVisible && 'lb-ad-visible')}
        style={{ minHeight: height || undefined }}
        {...{
          [RENDER_STATE_ATTR_NAME]: bannerState,
          [NAME_ATTR_NAME]: name.toLowerCase(),
        }}
      >
        {showFallback && <Fallback />}
        {showPlaceholder && <Placeholder />}
        <div
          ref={adsEl}
          id={domId}
          className={styles.banner}
        />
      </div>
    </>
  );
}

Adfox.defaultProps = {
  placeholder: DefaultPlaceholder,
  platformType: 'adfox',
};

Adfox.propTypes = {
  ownerId: PropTypes.number,
  blockId: PropTypes.string,
  params: PropTypes.object,

  /**
   * Компонент отображаемый до того, как баннер будет доступен пользователю.
   */
  placeholder: PropTypes.func,

  /**
   * Компонент отображаемый пользователю в случае отсутствия баннера.
   */
  fallback: PropTypes.func,

  /**
   * Уникальный id плейсхолдера.
   *
   * Если не указан автоматически формируется на основе глобального
   * инкрементального счетчика.
   *
   * По нему будет инициализироваться баннер и он должен быть уникальным
   * в рамках страниц.
   */
  domId: PropTypes.string.isRequired,

  /**
   * Начальная высота рекламного места.
   *
   * Используется для резервирования места под плейсхолдер куда позже будет
   * вставлен баннер. Такое резервирование позволит сократить до минимум
   * пересчет лейаута при отображении рекламы.
   */
  height: PropTypes.number,

  /**
   * Название размешения
   */
  name: PropTypes.string,

  /**
   * Функцию рендерится с параметрами сайта, которых нет в LB
   */
  onRender: PropTypes.func,

  /**
   * Рекламная платформа adfox или context
   */
  platformType: PropTypes.oneOf(Object.keys(mapPlatformTypes)),

  /**
   * Настройки lazyload
   */
  lazyLoad: PropTypes.object,
};

export default withRequestId(withResolveDomId(Adfox));
